import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import CalendarShareGuide from '../../components/LeadsVo/CalendarShareGuide';
import { RDV_LOCATION_OPTIONS } from '../../components/LeadsVo/RdvLocationEditor';

import { useAuth } from '../../components/AuthContext';
import {
  useLeadsVoAgent,
  useResearchOptions,
  useSearchCities,
  useUpdateBusinessHours,
  useUpdateCalendar,
  useUpdateProfile,
  useUpdateResearch,
  useUpdateZone,
} from '../../hooks/useLeadsVoQueries';
import { useGooglePlacesAutocomplete } from '../../hooks/useGooglePlacesAutocomplete';
import { hasPuretechExcluded, togglePuretechExclusion } from '../../utils/leadsVoMotorisations';

const STEPS = ['Profil', 'Calendrier Google (optionnel)', 'Horaires', 'Recherche & Zone'];

const btnPrimary = {
  backgroundColor: '#FDBB5F',
  color: '#262626',
  textTransform: 'none' as const,
  fontWeight: 600,
  px: 4,
  '&:hover': { backgroundColor: '#fcae4d' },
  '&:disabled': { opacity: 0.5 },
};

const chipSelected = { backgroundColor: '#262626', color: 'white', fontWeight: 600 };
const chipUnselected = { backgroundColor: 'white', color: '#262626', border: '1px solid #E0E0E0', '&:hover': { borderColor: '#FDBB5F' } };

type Slot = { start: string; end: string };
type BusinessHours = Record<string, Slot[]>;

const WEEKDAYS = [
  { key: 'lundi', label: 'Lundi' },
  { key: 'mardi', label: 'Mardi' },
  { key: 'mercredi', label: 'Mercredi' },
  { key: 'jeudi', label: 'Jeudi' },
  { key: 'vendredi', label: 'Vendredi' },
  { key: 'samedi', label: 'Samedi' },
  { key: 'dimanche', label: 'Dimanche' },
];

const defaultBusinessHours = (): BusinessHours => ({
  lundi: [{ start: '09:00', end: '12:30' }, { start: '14:00', end: '19:00' }],
  mardi: [{ start: '09:00', end: '12:30' }, { start: '14:00', end: '19:00' }],
  mercredi: [{ start: '09:00', end: '12:30' }, { start: '14:00', end: '19:00' }],
  jeudi: [{ start: '09:00', end: '12:30' }, { start: '14:00', end: '19:00' }],
  vendredi: [{ start: '09:00', end: '12:30' }, { start: '14:00', end: '19:00' }],
  samedi: [{ start: '10:00', end: '17:00' }],
  dimanche: [],
});

const RADIUS_OPTIONS = [1, 3, 5, 10, 20, 50];

// ─── Step 1: Profil ───

const StepProfile = ({
  profile,
  setProfile,
  validatedAddress,
  setValidatedAddress,
  error,
}: {
  profile: any;
  setProfile: (p: any) => void;
  validatedAddress: string;
  setValidatedAddress: (v: string) => void;
  error: string;
}) => {
  const addressRef = useRef<HTMLInputElement>(null);
  // Sélection via Google Places : on met à jour les deux valeurs en parallèle. Le
  // validatedAddress reste figé tant que l'utilisateur ne reclique pas une suggestion
  // — toute saisie manuelle après coup fait diverger profile.agency_address de
  // validatedAddress, ce qui bloque le Continue.
  const handleAddressSelect = useCallback(
    (address: string) => {
      setProfile({ ...profile, agency_address: address });
      setValidatedAddress(address);
    },
    [profile, setProfile, setValidatedAddress],
  );
  useGooglePlacesAutocomplete(addressRef, handleAddressSelect);

  return (
  <Box display="flex" flexDirection="column" gap={3}>
    <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#262626' }}>Votre profil</Typography>
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <TextField label="Prénom" value={profile.first_name} onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} fullWidth required placeholder="Ex: Marie" />
      </Grid>
      <Grid item xs={6}>
        <TextField label="Nom" value={profile.last_name} onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} fullWidth required placeholder="Ex: Dupont" />
      </Grid>
    </Grid>
    <TextField label="Nom commercial (ex : EWIGO, Ma Concession…)" value={profile.raison_sociale} onChange={(e) => setProfile({ ...profile, raison_sociale: e.target.value })} fullWidth />

    <Box>
      <Typography sx={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: '#464646', mb: 1 }}>
        Lieu de rendez-vous
      </Typography>
      <Box display="flex" gap={1}>
        {RDV_LOCATION_OPTIONS.map((opt) => (
          <Chip
            key={opt.value}
            label={opt.label}
            onClick={() => setProfile({ ...profile, rdv_location: opt.value })}
            sx={{
              cursor: 'pointer',
              fontSize: 13,
              px: 1,
              ...(profile.rdv_location === opt.value ? chipSelected : chipUnselected),
            }}
          />
        ))}
      </Box>
    </Box>

    {(() => {
      // L'adresse est dite "non validée" si elle a été tapée (ou modifiée) mais
      // n'a pas été sélectionnée dans la liste Google Places. On affiche le champ
      // en rouge avec un message d'aide direct (pas besoin d'attendre Continue).
      const typed = profile.agency_address.trim();
      const needsAutocomplete = typed.length > 0 && typed !== validatedAddress.trim();
      // Champ toujours monté (masqué pour « Au domicile ») : l'autocomplete Google ne se
      // branche qu'une fois, sur l'input présent au montage.
      return (
        <Box sx={{ display: profile.rdv_location === 'home' ? 'none' : 'block' }}>
          <TextField
            label={profile.rdv_location === 'agency' ? "Adresse de l'agence (où les particuliers se rendent)" : "Adresse de l'agence (optionnel, pour les RDV en agence)"}
            value={profile.agency_address}
            onChange={(e) => setProfile({ ...profile, agency_address: e.target.value })}
            fullWidth
            required={profile.rdv_location === 'agency'}
            inputRef={addressRef}
            placeholder="Commencez à taper pour rechercher"
            error={needsAutocomplete}
            helperText={needsAutocomplete ? 'Sélectionnez une adresse dans la liste de suggestions Google.' : ''}
          />
        </Box>
      );
    })()}

    {error && <Typography sx={{ color: '#C62828', fontSize: 14 }}>{error}</Typography>}
  </Box>
  );
};

// ─── Step 2: Calendar ───

const StepCalendar = ({ calendarId, setCalendarId, error }: { calendarId: string; setCalendarId: (v: string) => void; error: string }) => (
  <Box display="flex" flexDirection="column" gap={3}>
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#262626' }}>Connexion Google Calendar</Typography>
      <Typography sx={{ color: '#464646', fontSize: 14, mt: 1 }}>
        Cette étape est <strong>optionnelle</strong> : vous pouvez la passer pour le moment. Pour connecter votre
        agenda dès maintenant, regardez la vidéo ci-dessous, partagez votre calendrier avec l'adresse Skipcar,
        puis collez l'identifiant de votre calendrier.
      </Typography>
    </Box>
    <CalendarShareGuide calendarId={calendarId} setCalendarId={setCalendarId} error={error} />
  </Box>
);

// ─── Step 3: Business Hours ───

const StepBusinessHours = ({ hours, setHours, error }: { hours: BusinessHours; setHours: (h: BusinessHours) => void; error: string }) => {
  const toggleOpen = (day: string) => {
    if ((hours[day] || []).length === 0) {
      setHours({ ...hours, [day]: [{ start: '09:00', end: '18:00' }] });
    } else {
      setHours({ ...hours, [day]: [] });
    }
  };

  const addSlot = (day: string) => {
    const cur = hours[day] || [];
    if (cur.length >= 2) return;
    setHours({ ...hours, [day]: [...cur, { start: '14:00', end: '18:00' }] });
  };

  const removeSlot = (day: string, idx: number) => {
    const cur = hours[day] || [];
    setHours({ ...hours, [day]: cur.filter((_, i) => i !== idx) });
  };

  const updateSlot = (day: string, idx: number, field: 'start' | 'end', value: string) => {
    const cur = [...(hours[day] || [])];
    cur[idx] = { ...cur[idx], [field]: value };
    setHours({ ...hours, [day]: cur });
  };

  const copyMondayToWeekdays = () => {
    const monday = hours.lundi || [];
    setHours({ ...hours, mardi: [...monday], mercredi: [...monday], jeudi: [...monday], vendredi: [...monday] });
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#262626' }}>Horaires d'ouverture</Typography>
        <Typography sx={{ color: '#464646', fontSize: 14, mt: 1 }}>
          Ces horaires sont utilisés pour planifier les rendez-vous et les rappels téléphoniques pendant vos plages d'ouverture.
        </Typography>
      </Box>

      <Box display="flex" justifyContent="flex-end">
        <Button size="small" onClick={copyMondayToWeekdays} sx={{ textTransform: 'none', color: '#FDBB5F', fontWeight: 600, fontSize: 12 }}>
          ↻ Copier lundi sur mar-ven
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
        {WEEKDAYS.map((d) => {
          const slots = hours[d.key] || [];
          const isOpen = slots.length > 0;
          return (
            <Box key={d.key} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1, borderBottom: '1px solid #F0F0F0' }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#262626', minWidth: 80 }}>{d.label}</Typography>
              <FormControlLabel
                control={<Checkbox checked={isOpen} onChange={() => toggleOpen(d.key)} size="small" sx={{ '&.Mui-checked': { color: '#FDBB5F' } }} />}
                label={<Typography sx={{ fontSize: 12, color: '#464646' }}>{isOpen ? 'Ouvert' : 'Fermé'}</Typography>}
                sx={{ minWidth: 90 }}
              />
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" flex={1}>
                {isOpen && slots.map((s, idx) => (
                  <Box key={idx} display="flex" alignItems="center" gap={0.5}>
                    <TextField type="time" value={s.start} onChange={(e) => updateSlot(d.key, idx, 'start', e.target.value)} size="small" sx={{ width: 110 }} inputProps={{ style: { fontSize: 13, fontFamily: 'monospace' } }} />
                    <Typography sx={{ color: '#464646', fontSize: 12 }}>–</Typography>
                    <TextField type="time" value={s.end} onChange={(e) => updateSlot(d.key, idx, 'end', e.target.value)} size="small" sx={{ width: 110 }} inputProps={{ style: { fontSize: 13, fontFamily: 'monospace' } }} />
                    {slots.length > 1 && (
                      <Button size="small" onClick={() => removeSlot(d.key, idx)} sx={{ color: '#C62828', minWidth: 'auto', fontSize: 12, p: 0 }}>×</Button>
                    )}
                  </Box>
                ))}
                {isOpen && slots.length < 2 && (
                  <Button size="small" onClick={() => addSlot(d.key)} sx={{ textTransform: 'none', color: '#FDBB5F', fontWeight: 600, fontSize: 12 }}>+ plage</Button>
                )}
              </Box>
            </Box>
          );
        })}
      </Paper>

      {error && <Typography sx={{ color: '#C62828', fontSize: 14 }}>{error}</Typography>}
    </Box>
  );
};

// ─── Step 4: Research + Zone ───

const ChipGroup = ({ options, selected, onToggle, onSelectAll, onClearAll }: {
  options: string[]; selected: string[]; onToggle: (v: string) => void; onSelectAll?: () => void; onClearAll?: () => void;
}) => (
  <Box>
    {(onSelectAll || onClearAll) && (
      <Box display="flex" gap={2} mb={1}>
        {onSelectAll && <Button size="small" onClick={onSelectAll} sx={{ textTransform: 'none', color: '#FDBB5F', fontSize: 11, p: 0, minWidth: 'auto' }}>Tout sélectionner</Button>}
        {onClearAll && <Button size="small" onClick={onClearAll} sx={{ textTransform: 'none', color: '#9E9E9E', fontSize: 11, p: 0, minWidth: 'auto' }}>Tout désélectionner</Button>}
      </Box>
    )}
    <Box display="flex" flexWrap="wrap" gap={0.75}>
      {options.map((o) => {
        const on = selected.includes(o);
        return (
          <Chip
            key={o}
            label={on ? `✓ ${o}` : o}
            onClick={() => onToggle(o)}
            size="small"
            sx={{ cursor: 'pointer', fontSize: 12, ...(on ? chipSelected : chipUnselected) }}
          />
        );
      })}
    </Box>
  </Box>
);

const BrandsPicker = ({ brands, selected, onToggle, onSelectAll, onClearAll }: {
  brands: string[]; selected: string[]; onToggle: (b: string) => void; onSelectAll: () => void; onClearAll: () => void;
}) => {
  const [q, setQ] = useState('');
  const filtered = brands.filter((b) => b.toLowerCase().includes(q.toLowerCase()));
  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <TextField value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer (ex: ren, peug, bmw…)" size="small" sx={{ flex: 1 }} inputProps={{ style: { fontSize: 13 } }} />
        <Button size="small" onClick={onSelectAll} sx={{ textTransform: 'none', color: '#FDBB5F', fontSize: 11, whiteSpace: 'nowrap', minWidth: 'auto' }}>Tout sélectionner</Button>
        <Button size="small" onClick={onClearAll} sx={{ textTransform: 'none', color: '#9E9E9E', fontSize: 11, whiteSpace: 'nowrap', minWidth: 'auto' }}>Tout désélectionner</Button>
      </Box>
      <Paper variant="outlined" sx={{ maxHeight: 220, overflow: 'auto', p: 1.5, borderRadius: 1 }}>
        <Box display="flex" flexWrap="wrap" gap={0.75}>
          {filtered.map((b) => {
            const on = selected.includes(b);
            return (
              <Chip
                key={b}
                label={on ? `✓ ${b}` : b}
                onClick={() => onToggle(b)}
                size="small"
                sx={{ cursor: 'pointer', fontSize: 11, ...(on ? chipSelected : chipUnselected) }}
              />
            );
          })}
          {filtered.length === 0 && <Typography sx={{ fontSize: 12, color: '#9E9E9E' }}>Aucune marque correspondante</Typography>}
        </Box>
      </Paper>
    </Box>
  );
};

const StepResearchZone = ({ zone, setZone, research, setResearch, error }: {
  zone: any; setZone: (z: any) => void; research: any; setResearch: (r: any) => void; error: string;
}) => {
  const { data: options, isLoading } = useResearchOptions();
  const [cityQuery, setCityQuery] = useState('');
  const { data: citiesData } = useSearchCities(cityQuery);
  const [defaultsApplied, setDefaultsApplied] = useState(false);

  useEffect(() => {
    if (!options || defaultsApplied) return;
    const updates: Record<string, any> = {};
    if (research.marques_voulues.length === 0) updates.marques_voulues = [...options.brands];
    if (research.carburants.length === 0) updates.carburants = [...options.carburants];
    if (research.boites.length === 0) updates.boites = [...options.boites];
    if (research.types_carrosserie.length === 0) updates.types_carrosserie = [...options.types_carrosserie];
    if (Object.keys(updates).length > 0) setResearch({ ...research, ...updates });
    setDefaultsApplied(true);
  }, [options, defaultsApplied, research, setResearch]);

  if (isLoading) return <CircularProgress sx={{ color: '#FDBB5F' }} />;
  if (!options) return null;

  const c = research;
  const toggleArray = (field: string, value: string) => {
    const cur = c[field] || [];
    setResearch({ ...c, [field]: cur.includes(value) ? cur.filter((x: string) => x !== value) : [...cur, value] });
  };

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: 18, color: '#262626' }}>Recherche véhicule</Typography>
        <Typography sx={{ color: '#464646', fontSize: 14, mt: 1 }}>
          Définissez votre zone géographique et les critères des véhicules recherchés. Ces paramètres sont modifiables à tout moment.
        </Typography>
      </Box>

      {/* Zone */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 16, color: '#262626', mb: 2 }}>Zone de chasse</Typography>
        <Typography sx={{ color: '#464646', fontSize: 14, mb: 2 }}>
          Choisissez comment définir votre zone : une liste de villes / codes postaux, ou une ville avec un rayon.
        </Typography>
        <RadioGroup value={zone.mode} onChange={(e) => setZone({ ...zone, mode: e.target.value })} sx={{ mb: 2 }}>
          <FormControlLabel value="cities" control={<Radio sx={{ '&.Mui-checked': { color: '#FDBB5F' } }} />} label="Par villes / codes postaux" />
          <FormControlLabel value="radius" control={<Radio sx={{ '&.Mui-checked': { color: '#FDBB5F' } }} />} label="Par rayon autour d'une ville" />
        </RadioGroup>

        {zone.mode === 'cities' && (
          <Autocomplete
            multiple
            freeSolo
            options={citiesData?.cities ?? []}
            value={zone.cities}
            onChange={(_, val) => setZone({ ...zone, cities: val })}
            onInputChange={(_, val) => setCityQuery(val)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => <Chip label={option} size="small" {...getTagProps({ index })} key={option} sx={{ backgroundColor: '#262626', color: 'white' }} />)
            }
            renderInput={(params) => <TextField {...params} placeholder="Tape une ville ou un CP…" />}
          />
        )}

        {zone.mode === 'radius' && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Autocomplete
              freeSolo
              options={citiesData?.cities ?? []}
              value={zone.center}
              onChange={(_, val) => setZone({ ...zone, center: val })}
              onInputChange={(_, val) => setCityQuery(val)}
              renderInput={(params) => <TextField {...params} label="Ville" placeholder="Ex: Bordeaux (33000)" />}
            />
            {zone.center && <Typography sx={{ color: '#2E7D32', fontSize: 12 }}>✓ Centre : {zone.center}</Typography>}
            <Box>
              <Typography sx={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: '#464646', mb: 1 }}>Rayon autour de la ville</Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {RADIUS_OPTIONS.map((km) => (
                  <Chip
                    key={km}
                    label={`${km} km`}
                    onClick={() => setZone({ ...zone, radius_km: km })}
                    sx={{ cursor: 'pointer', fontWeight: 600, fontSize: 13, px: 1, ...(zone.radius_km === km ? chipSelected : chipUnselected) }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Marques */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography sx={{ fontWeight: 600, fontSize: 16, color: '#262626' }}>Marques acceptées ({c.marques_voulues.length}/{options.brands.length})</Typography>
          <Box display="flex" gap={2}>
            <Button size="small" onClick={() => setResearch({ ...c, marques_voulues: [...options.brands] })} sx={{ textTransform: 'none', color: '#FDBB5F', fontSize: 12, minWidth: 'auto' }}>Tout sélectionner</Button>
            <Button size="small" onClick={() => setResearch({ ...c, marques_voulues: [] })} sx={{ textTransform: 'none', color: '#9E9E9E', fontSize: 12, minWidth: 'auto' }}>Tout désélectionner</Button>
          </Box>
        </Box>
        <BrandsPicker
          brands={options.brands}
          selected={c.marques_voulues}
          onToggle={(b) => toggleArray('marques_voulues', b)}
          onSelectAll={() => setResearch({ ...c, marques_voulues: [...options.brands] })}
          onClearAll={() => setResearch({ ...c, marques_voulues: [] })}
        />
      </Paper>

      {/* Carburant, boîte, carrosserie */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 16, color: '#262626', mb: 2 }}>Carburant, boîte & carrosserie</Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          <Autocomplete
            multiple
            options={options.carburants}
            value={c.carburants}
            onChange={(_, val) => setResearch({ ...c, carburants: val })}
            renderInput={(params) => <TextField {...params} label="Carburants" placeholder="Sélectionner..." />}
          />
          <Autocomplete
            multiple
            options={options.boites}
            value={c.boites}
            onChange={(_, val) => setResearch({ ...c, boites: val })}
            renderInput={(params) => <TextField {...params} label="Boîte de vitesses" placeholder="Sélectionner..." />}
          />
          <Autocomplete
            multiple
            options={options.types_carrosserie}
            value={c.types_carrosserie}
            onChange={(_, val) => setResearch({ ...c, types_carrosserie: val })}
            renderInput={(params) => <TextField {...params} label="Types de carrosserie" placeholder="Sélectionner..." />}
          />
        </Box>
      </Paper>

      {/* Année, km, prix */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 16, color: '#262626', mb: 2 }}>Année, kilométrage, prix</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}><TextField label="Année min (optionnel)" type="number" value={c.annee_min ?? ''} onChange={(e) => setResearch({ ...c, annee_min: e.target.value === '' ? null : Number(e.target.value) })} fullWidth size="small" placeholder="ex: 2015" inputProps={{ style: { fontFamily: 'monospace' } }} /></Grid>
          <Grid item xs={6}><TextField label="Année max (optionnel)" type="number" value={c.annee_max ?? ''} onChange={(e) => setResearch({ ...c, annee_max: e.target.value === '' ? null : Number(e.target.value) })} fullWidth size="small" placeholder="ex: 2024" inputProps={{ style: { fontFamily: 'monospace' } }} /></Grid>
          <Grid item xs={6}><TextField label="Kilométrage min (optionnel)" type="number" value={c.km_min ?? ''} onChange={(e) => setResearch({ ...c, km_min: e.target.value === '' ? null : Number(e.target.value) })} fullWidth size="small" placeholder="ex: 0" inputProps={{ style: { fontFamily: 'monospace' } }} /></Grid>
          <Grid item xs={6}><TextField label="Kilométrage max (optionnel)" type="number" value={c.km_max ?? ''} onChange={(e) => setResearch({ ...c, km_max: e.target.value === '' ? null : Number(e.target.value) })} fullWidth size="small" placeholder="ex: 150000" inputProps={{ style: { fontFamily: 'monospace' } }} /></Grid>
          <Grid item xs={6}><TextField label="Prix min (€, optionnel)" type="number" value={c.prix_min_eur ?? ''} onChange={(e) => setResearch({ ...c, prix_min_eur: e.target.value === '' ? null : Number(e.target.value) })} fullWidth size="small" placeholder="ex: 3000" inputProps={{ style: { fontFamily: 'monospace' } }} /></Grid>
          <Grid item xs={6}><TextField label="Prix max (€, optionnel)" type="number" value={c.prix_max_eur ?? ''} onChange={(e) => setResearch({ ...c, prix_max_eur: e.target.value === '' ? null : Number(e.target.value) })} fullWidth size="small" placeholder="ex: 25000" inputProps={{ style: { fontFamily: 'monospace' } }} /></Grid>
        </Grid>
      </Paper>

      {/* Filtres qualité */}
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 1 }}>
        <Typography sx={{ fontWeight: 600, fontSize: 16, color: '#262626', mb: 2 }}>Filtres qualité</Typography>
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: 14, color: '#464646' }}>Exclure les véhicules accidentés / déclarés en réparation</Typography>
            <Switch checked={c.exclure_accidentes} onChange={(e) => setResearch({ ...c, exclure_accidentes: e.target.checked })} sx={{ '& .Mui-checked': { color: '#FDBB5F' }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: '#FDBB5F' } }} />
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: 14, color: '#464646' }}>Exclure les véhicules d'import</Typography>
            <Switch checked={c.exclure_import} onChange={(e) => setResearch({ ...c, exclure_import: e.target.checked })} sx={{ '& .Mui-checked': { color: '#FDBB5F' }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: '#FDBB5F' } }} />
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: 14, color: '#464646' }}>Exclure les moteurs PureTech</Typography>
            <Switch checked={hasPuretechExcluded(c.motorisations_exclues)} onChange={(e) => setResearch({ ...c, motorisations_exclues: togglePuretechExclusion(c.motorisations_exclues, e.target.checked) })} sx={{ '& .Mui-checked': { color: '#FDBB5F' }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: '#FDBB5F' } }} />
          </Box>
        </Box>
      </Paper>

      {error && <Typography sx={{ color: '#C62828', fontSize: 14 }}>{error}</Typography>}
    </Box>
  );
};

// ─── Main Onboarding ───

const Onboarding = () => {
  const { currentUser } = useAuth();
  const { data: agentData, isLoading } = useLeadsVoAgent();
  const updateProfile = useUpdateProfile();
  const updateZone = useUpdateZone();
  const updateResearch = useUpdateResearch();
  const updateBusinessHours = useUpdateBusinessHours();
  const updateCalendar = useUpdateCalendar();

  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [prefilled, setPrefilled] = useState(false);

  const [profile, setProfile] = useState({
    first_name: '', last_name: '', raison_sociale: '',
    agency_address: '', rdv_location: 'agency',
  });
  // Adresse acceptée : copie de la dernière valeur sélectionnée via Google Places.
  // Si profile.agency_address diverge (saisie manuelle), on bloque le Continue.
  const [validatedAddress, setValidatedAddress] = useState('');
  const [calendarId, setCalendarId] = useState('');
  const [hours, setHours] = useState<BusinessHours>(defaultBusinessHours());
  const [zone, setZone] = useState<any>({ mode: 'cities', cities: [], center: '', radius_km: null });
  const [research, setResearch] = useState<any>({
    marques_voulues: [], marques_exclues: [], carburants: [], boites: [],
    types_carrosserie: [], prix_min_eur: 0, prix_max_eur: null,
    annee_min: null, annee_max: null, km_max: null,
    exclure_accidentes: false, exclure_import: false, motorisations_exclues: [],
  });

  useEffect(() => {
    if (prefilled) return;

    if (agentData?.partner) {
      const p = agentData.partner;
      setProfile({
        first_name: p.first_name ?? '', last_name: p.last_name ?? '',
        raison_sociale: p.raison_sociale ?? '',
        agency_address: p.agency_address ?? '', rdv_location: p.rdv_location ?? 'agency',
      });
      // L'adresse pré-remplie est considérée comme déjà validée. Si l'utilisateur la
      // modifie sans repasser par Google Places, la valeur divergera et bloquera.
      setValidatedAddress(p.agency_address ?? '');
      if (p.zone_mode) {
        setZone({ mode: p.zone_mode, cities: p.zone_cities ?? [], center: p.zone_center ?? '', radius_km: p.zone_radius_km ?? null });
      }
      if (p.business_hours && Object.keys(p.business_hours).length > 0) setHours(p.business_hours);
      if (p.google_cal_calendar_id) setCalendarId(p.google_cal_calendar_id);
      setPrefilled(true);
    } else if (currentUser?.data) {
      const d = currentUser.data;
      setProfile((prev: any) => ({
        ...prev,
        first_name: prev.first_name || d.first_name || '',
        last_name: prev.last_name || d.last_name || '',
        raison_sociale: prev.raison_sociale || d.organization?.name || '',
      }));
      setPrefilled(true);
    }
  }, [agentData, currentUser, prefilled]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: '#FDBB5F' }} />
      </Box>
    );
  }

  if (agentData?.partner?.statut === 'active') {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 5, textAlign: 'center', borderRadius: 2, borderTop: '4px solid #2E7D32' }}>
          <Typography sx={{ fontWeight: 900, fontSize: 24, color: '#262626', mb: 1 }}>Votre compte RDV Qualifiés est actif</Typography>
          <Typography sx={{ color: '#464646' }}>Vous pouvez commander des packs de RDV depuis l'onglet « Mes Packs ».</Typography>
        </Paper>
      </Container>
    );
  }

  const validate = (): string | null => {
    if (activeStep === 0) {
      if (!profile.first_name.trim() || !profile.last_name.trim()) return 'Prénom et nom sont requis.';
      const addressNeeded = profile.rdv_location === 'agency';
      const addressShown = profile.rdv_location !== 'home';
      const addressTyped = addressShown ? profile.agency_address.trim() : '';
      if (addressNeeded && !addressTyped) return "Adresse d'agence requise pour les RDV en agence.";
      // Pour les RDV en agence (et tout cas où une adresse a été tapée), on exige
      // qu'elle vienne d'une suggestion Google — sinon il manque souvent le CP et la
      // ville et le partenaire ne peut pas géolocaliser.
      if (addressTyped && addressTyped !== validatedAddress.trim()) {
        return "Choisissez une adresse dans la liste de suggestions Google qui s'affiche en tapant.";
      }
    }
    // Étape 1 (calendrier) : optionnelle — aucune validation bloquante, l'agence peut la passer.
    if (activeStep === 2) {
      const anyOpen = Object.values(hours).some((s) => Array.isArray(s) && s.length > 0);
      if (!anyOpen) return 'Indique au moins un jour d\'ouverture.';
    }
    if (activeStep === 3) {
      if (zone.mode === 'cities' && zone.cities.length === 0) return 'Sélectionne au moins une ville / CP.';
      if (zone.mode === 'radius' && (!zone.center || !zone.radius_km)) return 'Sélectionne une ville et un rayon.';
    }
    return null;
  };

  const handleNext = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      setError('');
      if (activeStep === 0) {
        // « Au domicile » : le champ adresse est masqué, sa saisie éventuelle n'est pas transmise.
        const { agency_address: _hiddenAddress, ...profileWithoutAddress } = profile;
        await updateProfile.mutateAsync(profile.rdv_location === 'home' ? profileWithoutAddress : profile);
      } else if (activeStep === 1) {
        await updateCalendar.mutateAsync(calendarId);
      } else if (activeStep === 2) {
        await updateBusinessHours.mutateAsync(hours);
      } else if (activeStep === 3) {
        if (zone.mode === 'cities') {
          await updateZone.mutateAsync({ mode: 'cities', cities: zone.cities });
        } else {
          await updateZone.mutateAsync({ mode: 'radius', center: zone.center, radius_km: zone.radius_km });
        }
        await updateResearch.mutateAsync({ criteres_vehicule: research });
      }
      if (activeStep < STEPS.length - 1) {
        setActiveStep((prev) => prev + 1);
      }
    } catch (err: any) {
      const raw = err.response?.data;
      if (raw?.error === 'calendar_not_shared' || err.response?.status === 409) {
        setError("Le calendrier n'est pas accessible. Vérifiez que vous avez bien partagé votre calendrier avec l'adresse email indiquée ci-dessus, avec la permission « Apporter des modifications aux événements ».");
      } else {
        const msg = raw?.detail || raw?.error || raw?.message || 'Une erreur est survenue';
        setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
    }
  };

  const isPending = updateProfile.isPending || updateCalendar.isPending || updateBusinessHours.isPending || updateZone.isPending || updateResearch.isPending;

  // L'étape calendrier étant optionnelle, le bouton invite à « Passer » tant qu'aucun identifiant n'est saisi.
  const nextLabel =
    activeStep === STEPS.length - 1
      ? 'Activer le compte'
      : activeStep === 1 && !calendarId.trim()
        ? 'Passer cette étape →'
        : 'Continuer →';

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography sx={{ fontWeight: 900, fontSize: 28, color: '#262626', mb: 1 }}>Activation RDV Qualifiés</Typography>
      <Typography sx={{ color: '#464646', fontSize: 14, mb: 4 }}>Activez votre compte en {STEPS.length} étapes</Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 5, '& .MuiStepIcon-root.Mui-active': { color: '#FDBB5F' }, '& .MuiStepIcon-root.Mui-completed': { color: '#2E7D32' } }}>
        {STEPS.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        {activeStep === 0 && (
          <StepProfile
            profile={profile}
            setProfile={setProfile}
            validatedAddress={validatedAddress}
            setValidatedAddress={setValidatedAddress}
            error={error}
          />
        )}
        {activeStep === 1 && <StepCalendar calendarId={calendarId} setCalendarId={setCalendarId} error={error} />}
        {activeStep === 2 && <StepBusinessHours hours={hours} setHours={setHours} error={error} />}
        {activeStep === 3 && <StepResearchZone zone={zone} setZone={setZone} research={research} setResearch={setResearch} error={error} />}
      </Paper>

      <Box mt={3} display="flex" justifyContent="space-between">
        <Button disabled={activeStep === 0} onClick={() => { setActiveStep((prev) => prev - 1); setError(''); }} sx={{ color: '#464646', textTransform: 'none', fontWeight: 500 }}>
          Précédent
        </Button>
        <Button onClick={handleNext} disabled={isPending} sx={btnPrimary}>
          {isPending ? <CircularProgress size={20} sx={{ color: '#262626' }} /> : nextLabel}
        </Button>
      </Box>
    </Container>
  );
};

export default Onboarding;
